// "Coming up" on a player's card (B-218): the rest of his season, a row a
// week — his club's opponent, home or away and the kickoff, or a bye — and at
// Standard, how that opponent has done against his position this season, in
// this league's scoring.
//
// A coworker's ask (DESIGN.md §2.8, 2026-09-10) and Ken's, 2026-09-27: to see
// when a player has a bye, or a hard stretch, and plan ahead. So the next four
// weeks show, the bye is said even when it is further out, and the rest of the
// season is a tap away.
//
// NUMBERS, NEVER A VERDICT (decisions/2026-09-12-tools-not-instructions.md):
// "12.1 a game to RB · 2nd fewest", never "tough" or "a good week to sit him".
// The rank is said the way it reads — most or fewest — so nobody has to know
// whether 1 means easy or hard. Since 2026-10-03 it is the matchup chip every
// row wears, tinted by which third it is in, beside the words (B-249).

import {
  formatInZone,
  valueOf,
  type PlayerCardView,
  type TeamRef,
  type UpcomingWeek,
} from '@fsp/domain';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtLeast } from '../../data/detail';
import { useLeagueBase, useReaderZone, useSchedule } from '../../data/hooks';
import { Icon } from '../../kit/Icon';
import { Button, Caption, Cover, Stack, Text } from '../../kit/ui';
import { MatchupChip, rankWords } from './MatchupChip';
import { clubLabel, positionLabelOf } from '../../sports/registry';

/** How many weeks show before "Show all". */
const AHEAD = 4;

// `rankWords` lives with the matchup chip now, and is still exported from here.
export { rankWords };

function WeekRow({
  w,
  position,
  zone,
  numbers,
  door,
}: {
  w: UpcomingWeek;
  position: string | null;
  zone: string | null;
  numbers: boolean;
  /** That week's fantasy matchup, when there is one to open (B-220). */
  door: { label: string; go: () => void } | null;
}) {
  const where = w.opponent === null ? '' : `${w.home ? 'vs' : 'at'} ${clubLabel(w.opponent)}`;
  const when =
    w.kickoff === null || zone === null
      ? ''
      : formatInZone(w.kickoff, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return (
    <div
      className={`fsp-row fsp-coming-week${door ? ' fsp-row-clickable fsp-cover-host' : ''}`}
      data-bye={w.state === 'bye' || undefined}
    >
      {door ? <Cover label={door.label} onClick={door.go} /> : null}
      <div className="fsp-coming-label">{w.label}</div>
      {w.state === 'bye' ? (
        <div className="fsp-coming-body">
          <Text weight="bold">Bye</Text>
        </div>
      ) : (
        <div className="fsp-coming-body">
          <div className="fsp-coming-opp">
            <Text weight="strong">{where}</Text>
            {when ? (
              <>
                {' '}
                <span className="fsp-sub">· {when}</span>
              </>
            ) : null}
          </div>
          {numbers && w.versus !== null && position !== null ? (
            <div className="fsp-sub fsp-num">
              {w.versus.perGame.toFixed(1)} a game to {positionLabelOf(position)} ·
              <MatchupChip cue={w.versus} position={position} opponent={w.opponent} always /> ·{' '}
              <span className="fsp-coming-games">
                {w.versus.games} {w.versus.games === 1 ? 'game' : 'games'}
              </span>
            </div>
          ) : null}
        </div>
      )}
      {door ? (
        <span className="fsp-coming-go" aria-hidden>
          <Icon name="next" />
        </span>
      ) : null}
    </div>
  );
}

export function ComingUp({
  season,
  position,
  holder,
  onGo,
}: {
  season: NonNullable<PlayerCardView['seasonSoFar']>;
  position: string | null;
  /** The team that holds him when it is not mine; null for mine, a free agent, waivers. */
  holder: TeamRef | null;
  /** Closes the card before going (a screen can stay mounted under it). */
  onGo: () => void;
}) {
  const numbers = useAtLeast('standard');
  const zone = useReaderZone();
  const [all, setAll] = useState(false);
  const navigate = useNavigate();
  const base = useLeagueBase();
  const schedule = valueOf(useSchedule());
  // A WEEK OPENS THAT WEEK'S MATCHUP (B-220): his team's when another
  // manager holds him, mine otherwise — mine is the one a manager planning
  // around him, or around adding him, is looking at. Only a week the league
  // plays and that team has a game in; the week stepper's `?period=` for mine,
  // so the matchup screen lands on that week.
  const doorFor = (w: UpcomingWeek): { label: string; go: () => void } | null => {
    const week = schedule?.periods.find((p) => p.period.index === w.period);
    if (!week) return null;
    const go = (to: string) => () => {
      onGo();
      navigate(to);
    };
    if (holder === null) {
      return week.myMatchup
        ? { label: `${w.label}: your matchup`, go: go(`${base}/matchup?period=${w.period}`) }
        : null;
    }
    const theirs = week.matchups.find(
      (m) => m.home.team.id === holder.id || m.away.team.id === holder.id,
    );
    return theirs
      ? { label: `${w.label}: ${holder.name}'s matchup`, go: go(`${base}/matchup/${theirs.id}`) }
      : null;
  };
  const weeks = season.upcoming;
  if (weeks.length === 0) return null;
  const shown = all ? weeks : weeks.slice(0, AHEAD);
  // THE BYE IS SAID EVEN WHEN IT IS FURTHER OUT: it is the one week a
  // manager plans around most.
  const laterBye = all ? null : (weeks.slice(AHEAD).find((w) => w.state === 'bye') ?? null);
  return (
    <Stack gap={1}>
      <Caption>Coming up</Caption>
      <div className="fsp-surface">
        {shown.map((w) => (
          <WeekRow
            key={w.windowId}
            w={w}
            position={position}
            zone={zone}
            numbers={numbers}
            door={doorFor(w)}
          />
        ))}
      </div>
      {laterBye !== null ? (
        <Text tone="secondary" block>
          Bye in {laterBye.label}.
        </Text>
      ) : null}
      {weeks.length > AHEAD ? (
        <div>
          <Button size="xs" variant="quiet" onClick={() => setAll((v) => !v)}>
            {all ? 'Show the next four' : `Show all ${weeks.length} weeks`}
          </Button>
        </div>
      ) : null}
    </Stack>
  );
}
